import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getFirebaseInstances } from '../../config/firebase/firebase';
import { BRAND_MESSAGING } from '../../constants/brandMessaging';
import styles from './LoginEnhanced.module.css';

const PasswordGate = () => {
  const navigate = useNavigate();
  const [accessCode, setAccessCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const isInviteCodeFormat = (value) => value.trim().toUpperCase().startsWith('VK-');

  // Check if already reserved
  useEffect(() => {
    const reservation = sessionStorage.getItem('signupInviteReservation');
    const passwordVerified = sessionStorage.getItem('signupPasswordVerified');
    if (reservation || passwordVerified === 'true') {
      navigate('/signup', { replace: true });
    }
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!accessCode) {
      setError('Access code is required');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const { app } = await getFirebaseInstances();
      const functions = getFunctions(app);
      try {
        const validateInvite = httpsCallable(functions, 'validateSignupInvite');
        const result = await validateInvite({ accessCode });
        sessionStorage.removeItem('signupPasswordVerified');
        sessionStorage.setItem('signupInviteReservation', JSON.stringify(result.data));
      } catch (inviteError) {
        if (isInviteCodeFormat(accessCode)) {
          throw inviteError;
        }

        const validatePassword = httpsCallable(functions, 'validateSignupPassword');
        await validatePassword({ password: accessCode });
        sessionStorage.removeItem('signupInviteReservation');
        sessionStorage.setItem('signupPasswordVerified', 'true');
      }

      navigate('/signup');
    } catch (err) {
      setError('Invalid or unavailable access code. Please try again.');
      setAccessCode('');
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setAccessCode(e.target.value);
    setError('');
  };

  return (
    <div className={styles.splitLayout}>
      {/* Left Panel: Branding & Trust */}
      <div className={styles.leftPanel}>
        <img
          src={`${process.env.PUBLIC_URL}/images/brand/verdekai-linkedin-company-logo.png`}
          alt="VerdeKai Legal Terminal"
          className={styles.brandImage}
        />
        <div className={styles.brandContent}>
          <h1 className={styles.platformName}>VerdeKai Legal Terminal</h1>
          <h2 className={styles.welcome}>{BRAND_MESSAGING.login.heading}</h2>
          <p className={styles.trustMsg}>{BRAND_MESSAGING.login.subheading}</p>
          <div className={styles.statsRow}>
            {BRAND_MESSAGING.login.stats.map((stat) => (
              <div key={stat.label}>
                <span className={styles.statValue}>{stat.value}</span>
                <span className={styles.statLabel}>{stat.label}</span>
              </div>
            ))}
          </div>
          <ul className={styles.trustList}>
            {BRAND_MESSAGING.login.trustBullets.map((bullet) => (
              <li key={bullet}><span className={styles.trustBadge}></span> {bullet}</li>
            ))}
          </ul>
        </div>
      </div>

      {/* Right Panel: Access Code Gate Form */}
      <div className={styles.rightPanel}>
        <form className={styles.formCard} onSubmit={handleSubmit} noValidate>
          <h2 className={styles.formTitle}>Access Required</h2>
          <p className={styles.formSubtitle}>
            Enter your beta access code to create an account
          </p>

          {error && <div className={styles.errorMsg}>{error}</div>}

          <div className={styles.formGroup}>
            <div className={styles.floatingLabelGroup}>
              <input
                type="text"
                name="accessCode"
                id="access-code"
                autoComplete="off"
                value={accessCode}
                onChange={handleInputChange}
                className={error ? styles.inputError : ''}
                required
                disabled={loading}
                autoFocus
              />
              <label
                htmlFor="access-code"
                className={accessCode ? styles.hasValue : ''}
              >
                Access Code
              </label>
            </div>
            {error && <span className={styles.errorText}>{error}</span>}
          </div>

          <button
            type="submit"
            className={styles.signInBtn}
            disabled={loading}
          >
            {loading ? 'Verifying...' : 'Continue'}
          </button>

          <div className={styles.signupPrompt}>
            Already have an account? <a href="/login">Sign in</a>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PasswordGate;
